import { FormControlLabel } from "@mui/material";
import { Alert, Checkbox, CurrencyInput, Grid, Input, NumericInput } from "../../../components";
import { FormikProps } from "formik";
import { formatMoney, nl2br } from "../../../utils";
import { useEffect, useMemo } from "react";
import { COMPANY_OPTIONS } from "../../../constants";
import { Program } from "../new-bid-form-step-one";
import { PriceSetting } from "../../programs/PriceSettingsInput";
import { CreateAccountFormValues } from "../hooks/useCreateAccountData";

interface DashboardData {
  last_seven_days_k_price: Record<string, number>[];
}

interface NewBidFormStepTwoProps {
    formik: FormikProps<{
        company: string;
        miles: string;
        price: string;
        pax: string;
        account_id: string;
        accepts_custom_payment: boolean;
        needs_email_redirect: boolean;
        }>;
    programs: Program[];
    programPriceSettings: PriceSetting[];
    dashboardData: DashboardData;
    createAccountFormik: FormikProps<CreateAccountFormValues>
}

export const NewBidFormStepTwo = ({formik, programPriceSettings, programs, dashboardData, createAccountFormik}: NewBidFormStepTwoProps) => {
    const paxOptions = useMemo(() => {
        return COMPANY_OPTIONS.find(item => item.value === formik.values.company)?.pax_options || []
    }, [formik.values.company])

    useEffect(() => {
      if (paxOptions.length === 1) {
          formik.setFieldValue('pax', paxOptions[0].value)
      } else {
          formik.setFieldValue('pax', '')
      }

      createAccountFormik.setFieldValue('company', formik.values.company)
  }, [formik.values.company, paxOptions])


    const selectedProgram = useMemo(() => {
        return (programs || []).find(
            (program: { company: string; active: boolean }) => {
            return program.company === formik.values.company;
            }
        );
    }, [formik.values.company, programs]);

    const lastPricePerK = useMemo(() => {
      const recent = dashboardData?.last_seven_days_k_price?.[dashboardData?.last_seven_days_k_price?.length - 1];

      return recent?.[formik.values.company];
    }, [dashboardData.last_seven_days_k_price, formik.values.company])
    
  return (
    <>
      <Grid item xs={12}>
        <NumericInput
          label="Total de Milhas para Venda"
          name="miles"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.miles}
          helperText={formik.touched.miles && formik.errors.miles}
          error={!!formik.touched.miles && !!formik.errors.miles}
        />
      </Grid>
      <Grid item xs={12}>
        <CurrencyInput
          label="Preço por 1.000 Milhas (R$)"
          name="price"
          onChangeValue={(_, originalValue) => {
            formik.setFieldValue("price", originalValue);
          }}
          onBlur={formik.handleBlur}
          value={formik.values.price}
          helperText={
            (formik.touched.price && formik.errors.price) ||
            (!programPriceSettings && lastPricePerK > 0
              ? `Valor sugerido ${formatMoney(lastPricePerK)}`
              : "")
          }
          error={!!formik.touched.price && !!formik.errors.price}
        />
      </Grid>
      <Grid item xs={12}>
      <Input 
          label="Quantidade de CPFs"
          name="pax"
          select
          options={paxOptions}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.pax}
          helperText={(formik.touched.pax && formik.errors.pax) || (paxOptions.length <= 1 && 'Esta companhia não possui limitação de CPFs')}
          error={!!formik.touched.pax && !!formik.errors.pax}
          disabled={paxOptions.length <= 1}
      />
      </Grid>

      {selectedProgram?.accepts_custom_payment && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                value={formik.values.accepts_custom_payment}
                onChange={(event) => {
                  formik.setFieldValue(
                    "accepts_custom_payment",
                    !formik.values.accepts_custom_payment,
                    true
                  );
                }}
              />
            }
            label="Aceita Pagamento à Prazo"
          />
        </Grid>
      )}
      {selectedProgram?.custom_bid_disclaimer && (
        <Grid item xs={12}>
          <Alert icon={false} color="info">
            <span
              dangerouslySetInnerHTML={{
                __html: nl2br(selectedProgram.custom_bid_disclaimer),
              }}
            />
          </Alert>
        </Grid>
      )}
    </>
  );
};
